import { selectSearchCompanyRadiusAreas } from './pcSelection.js';
import { makeDraggable, setCommand, setStatus } from './mapUtils.js';
import { POI_ANALYSIS_ACTIONS, getPoiAnalysisAction } from './poiAnalysisActions.js';
import { runPoiDtmAnalysis, runPoiSetDtmAnalysis } from './poiAnalysisService.js';

let backdrop = null;
let analysisTarget = null;
const monitoredPoiLayers = new WeakSet();

function setDocked(docked) {
  if (!backdrop) return;
  backdrop.classList.toggle('is-docked-left', docked);
  const dialog = backdrop.querySelector('.poi-analysis-dialog');
  const button = backdrop.querySelector('.poi-analysis-dock');
  dialog?.setAttribute('aria-modal', String(!docked));
  ['position', 'left', 'top', 'right', 'bottom'].forEach(property => dialog?.style.removeProperty(property));
  button?.classList.toggle('is-active', docked);
  if (button) {
    button.title = docked ? 'Return to centered dialog' : 'Dock to left';
    button.setAttribute('aria-label', button.title);
    button.setAttribute('aria-pressed', String(docked));
  }
}

function closeDialog() {
  if (backdrop) { backdrop.hidden = true; setDocked(false); }
  analysisTarget = null;
}

function updateInput(action) {
  const input = backdrop?.querySelector('.poi-analysis-value');
  const unit = backdrop?.querySelector('.poi-analysis-unit');
  const inputLabel = backdrop?.querySelector('.poi-analysis-input-label');
  if (!input || !unit || !inputLabel || !action) return;
  inputLabel.textContent = action.valueLabel || 'Value';
  input.placeholder = action.placeholder;
  input.setAttribute('aria-label', action.placeholder);
  input.value = '';
  unit.textContent = action.unit;
  input.focus({ preventScroll: true });
}

function updateOnlySelectionOption() {
  const checkbox = backdrop?.querySelector('.poi-analysis-only-selection input');
  const label = backdrop?.querySelector('.poi-analysis-only-selection');
  if (!checkbox || !label) return;
  const layers = analysisTarget?.targetType === 'poi-set' ? analysisTarget.layers || [] : [];
  const available = layers.some(layer => layer?.getVisible?.() !== false && (layer?.getSource?.()?.getFeatures?.()?.length || 0) > 0);
  checkbox.disabled = !available;
  if (!available) checkbox.checked = false;
  label.classList.toggle('is-disabled', !available);
  label.title = available ? 'Limit analysis to POI categories visible in Layer Info' : 'No visible POI category is selected';
}

async function submitAnalysis(form) {
  const errorElement = form.querySelector('.poi-analysis-error');
  const action = getPoiAnalysisAction(new FormData(form).get('poi-analysis-mode'));
  const value = Number(form.querySelector('.poi-analysis-value').value);
  const onlySelection = form.querySelector('.poi-analysis-only-selection input')?.checked === true;
  if (!action || !Number.isFinite(value) || value <= 0) {
    errorElement.textContent = 'Enter a value greater than zero.'; errorElement.hidden = false; return;
  }
  const submitButton = form.querySelector('button[type="submit"]');
  submitButton.disabled = true; submitButton.textContent = 'Loading...'; errorElement.hidden = true;
  try {
    const isSet = analysisTarget?.targetType === 'poi-set';
    if (!isSet && !analysisTarget?.feature) throw new Error('The selected POI is no longer available. Please select it again.');
    const result = isSet
      ? await runPoiSetDtmAnalysis({ mode: action.mode, value, poiSetId: analysisTarget.poiSetId, layers: analysisTarget.layers || [], onlySelection })
      : action.mode === 'radius' && analysisTarget.searchCompany === true
        ? await selectSearchCompanyRadiusAreas({ feature: analysisTarget.feature, radiusKm: value })
        : await runPoiDtmAnalysis({ mode: action.mode, value, feature: analysisTarget.feature });
    document.dispatchEvent(new CustomEvent(isSet ? 'poi-set-analysis-requested' : 'poi-analysis-requested', {
      detail: { ...analysisTarget, analysisType: action.type, mode: action.mode, label: action.label, value, unit: action.unit, onlySelection, result }
    }));
    const level = result.level || result.requestBody.level;
    setCommand(`${action.label}: ${result.selectedFeatures.length} ${String(level).toUpperCase()} area(s) selected`);
    setStatus(`${result.selectedFeatures.length} postcode area(s) selected`);
    closeDialog();
  } catch (error) {
    const apiMessage = error?.message || 'Proximity analysis failed.';
    errorElement.textContent = /selection\s*type.*invalid|type.*invalid/i.test(apiMessage)
      ? `${action.optionLabel || action.valueLabel || 'Selected option'} is to be implemented.`
      : apiMessage;
    errorElement.hidden = false;
    setStatus(errorElement.textContent); setCommand('Unable to complete proximity analysis');
  } finally {
    submitButton.disabled = false; submitButton.textContent = 'Continue';
  }
}

function createDialog() {
  const element = document.createElement('div');
  element.className = 'poi-analysis-dialog-backdrop'; element.hidden = true;
  element.innerHTML = `<section class="poi-analysis-dialog" role="dialog" aria-modal="true" aria-labelledby="poi-analysis-title"><header class="poi-analysis-dialog-header"><div><span class="poi-analysis-eyebrow">Point of Interest</span><h6 id="poi-analysis-title">Proximity analysis</h6></div><div class="poi-analysis-header-actions"><button type="button" class="btn btn-sm btn-outline-secondary sevenn-panel-toggle poi-analysis-dock" title="Dock to left" aria-label="Dock to left" aria-pressed="false"><i class="bi bi-dash-lg" aria-hidden="true"></i></button><button type="button" class="poi-analysis-close" aria-label="Close">&times;</button></div></header><form class="poi-analysis-form"><fieldset class="poi-analysis-options"><legend>Analysis type</legend>${POI_ANALYSIS_ACTIONS.map(action => `<label title="${action.label}" aria-label="${action.label}"><input type="radio" name="poi-analysis-mode" value="${action.mode}" aria-label="${action.label}"><i class="bi ${action.icon}" aria-hidden="true"></i><span class="visually-hidden">${action.optionLabel}</span></label>`).join('')}</fieldset><label class="poi-analysis-input-label" for="poi-analysis-value">Value</label><div class="poi-analysis-input-group"><input id="poi-analysis-value" class="poi-analysis-value" type="number" min="0.01" step="any" required><span class="poi-analysis-unit"></span></div><div class="poi-analysis-error" role="alert" hidden></div><div class="poi-analysis-dialog-actions"><button type="submit" class="btn btn-sm btn-primary">Continue</button></div></form></section>`;
  const onlySelectionLabel = document.createElement('label');
  onlySelectionLabel.className = 'poi-analysis-only-selection';
  onlySelectionLabel.innerHTML = '<input type="checkbox"><span>Only Selected</span>';
  element.querySelector('.poi-analysis-error').before(onlySelectionLabel);
  const footer = document.createElement('div');
  footer.className = 'poi-analysis-footer';
  const error = element.querySelector('.poi-analysis-error');
  error.before(footer);
  footer.append(onlySelectionLabel, element.querySelector('.poi-analysis-dialog-actions'));
  element.querySelector('.poi-analysis-dock')?.remove();
  element.addEventListener('pointerdown', event => event.stopPropagation());
  element.querySelector('#poi-analysis-title').textContent = 'Location based selection';
  element.querySelectorAll('input[name="poi-analysis-mode"]').forEach(radio => radio.addEventListener('change', event => updateInput(getPoiAnalysisAction(event.currentTarget.value))));
  element.querySelector('.poi-analysis-close').onclick = closeDialog;
  const dialog = element.querySelector('.poi-analysis-dialog');
  const header = element.querySelector('.poi-analysis-dialog-header');
  header.addEventListener('pointerdown', event => {
    if (event.target.closest('button, input, select, textarea, a, label, [data-no-drag]')) return;
    if (element.classList.contains('is-docked-left')) setDocked(false);
    const rect = dialog.getBoundingClientRect();
    dialog.style.setProperty('position', 'fixed', 'important'); dialog.style.setProperty('left', `${rect.left}px`, 'important'); dialog.style.setProperty('top', `${rect.top}px`, 'important');
  });
  makeDraggable(dialog, header);
  element.querySelector('form').addEventListener('submit', event => { event.preventDefault(); submitAnalysis(event.currentTarget); });
  document.body.appendChild(element);
  return element;
}

export function openProximityAnalysisDialog(action, target) {
  if (!action || !target) return;
  backdrop ||= createDialog();
  analysisTarget = { ...target };
  (analysisTarget.layers || []).forEach(layer => {
    if (monitoredPoiLayers.has(layer)) return;
    layer?.on?.('change:visible', updateOnlySelectionOption);
    monitoredPoiLayers.add(layer);
  });
  backdrop.hidden = false;
  backdrop.querySelector('.poi-analysis-eyebrow').textContent = target.targetType === 'poi-set' ? 'Point of Interest Set' : 'Point of Interest';
  const locationName = target.feature?.get?.('name') || target.feature?.get?.('label') || target.feature?.get?.('title') || target.feature?.get?.('identifier') || 'Selected location';
  const poiSetName = target.poiSetName || target.layer?.get?.('poiSetName') || target.layer?.get?.('title') || 'Point of Interest Set';
  backdrop.querySelector('#poi-analysis-title').textContent = target.targetType === 'poi-set'
    ? String(poiSetName)
    : `${locationName} - ${poiSetName}`;
  const radio = backdrop.querySelector(`input[value="${action.mode}"]`);
  if (radio) radio.checked = true;
  backdrop.querySelector('.poi-analysis-error').hidden = true;
  const onlySelection = backdrop.querySelector('.poi-analysis-only-selection input');
  if (onlySelection) onlySelection.checked = true;
  updateOnlySelectionOption();
  updateInput(action);
}
